import { inject, Injectable } from '@angular/core';
import { map, of, switchMap, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { TranslateService } from '@ngx-translate/core';
import { convertGalaxyFilters, CRMSelectAllOptions } from '@galaxy/crm/static';
import { CRMExportService } from '@vendasta/crm';

function downloadFile(file: Blob, filename: string): void {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.setAttribute('style', 'display:none;');
  document.body.appendChild(link);
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

@Injectable({ providedIn: 'root' })
export class ExportActionsService {
  confirmationModal = inject(OpenConfirmationModalService);
  snackbarService = inject(SnackbarService);
  translateService = inject(TranslateService);
  exportService = inject(CRMExportService);

  async openSelectAllExportDialog(namespace: string, objectType: string, options?: CRMSelectAllOptions): Promise<void> {
    let objectTypeString = '';
    if (objectType === 'Company') {
      objectTypeString = this.translateService.instant('OBJECT_TYPES.COMPANY_PLURAL');
    } else if (objectType === 'Contact') {
      objectTypeString = this.translateService.instant('OBJECT_TYPES.CONTACT_PLURAL');
    }

    this.confirmationModal
      .openModal({
        title: this.translateService.instant('ACTIONS.CRM.EXPORT.TITLE', { objectType: objectTypeString }),
        message: this.translateService.instant('ACTIONS.CRM.EXPORT.CONFIRMATION_MESSAGE', {
          objectType: objectTypeString,
          total: options?.totalObjects || 0,
        }),
        confirmButtonText: this.translateService.instant('ACTIONS.CRM.EXPORT.CONFIRM'),
        cancelButtonText: this.translateService.instant('ACTIONS.CRM.EXPORT.CANCEL'),
      })
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            this.snackbarService.openSuccessSnack(this.translateService.instant('ACTIONS.CRM.EXPORT.DOWNLOADING'));
            const filters = convertGalaxyFilters(options?.filters);
            return this.exportService.exportCSV(namespace, objectType, options?.search, filters);
          }
          return of(null);
        }),
        map((response) => {
          if (response) {
            downloadFile(response.blob, response.filename);
            return true;
          }
          return false;
        }),
        take(1),
      )
      .subscribe({
        next: (completed) => {
          if (completed) {
            this.snackbarService.openSuccessSnack(
              this.translateService.instant('ACTIONS.CRM.EXPORT.SUCCESS', { objectType: objectTypeString }),
            );
          }
        },
        error: (err) => {
          console.error(err);
          this.snackbarService.openErrorSnack(
            this.translateService.instant('ACTIONS.CRM.EXPORT.ERROR', { objectType: objectTypeString }),
          );
        },
      });
  }
}
